import { format, parseISO } from 'date-fns';
import { Dot, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useGetClassAttendanceStatus } from '~entities/classes/api/get-class-attendance-status';
import { useUpdateAttendanceMutation } from '~entities/classes/api/update-attendance';
import type { Student } from '~entities/student/modal/student';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { getErrorMessage } from '~shared/utils/error-handler';

interface PresentStatusSelectorProps {
  data: Student;
  classId: string;
  // info: CellContext<Student, Array<Attendance | null>>;
  date: string;
  className?: string;
}

export const PresentStatusSelector = ({
  data,
  classId,
  date,
  className,
}: PresentStatusSelectorProps) => {
  const formattedDate = format(parseISO(date), 'yyyy-MM-dd');
  const { mutateAsync: updateAttendance, isPending: isUpdatingAttendance } =
    useUpdateAttendanceMutation();
  const { data: attendanceStatusData } = useGetClassAttendanceStatus();

  const [showDialog, setShowDialog] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState<{
    id: string;
    isPresent: boolean;
  } | null>(null);
  const [updatingType, setUpdatingType] = useState<'present' | 'absent' | null>(
    null,
  );

  const isUpdatingToPresent =
    isUpdatingAttendance && updatingType === 'present';
  const isUpdatingToAbsent = isUpdatingAttendance && updatingType === 'absent';

  const attendanceData = data.attendance[0]?.attendanceId;

  const presentStatus =
    attendanceData?.isPresent === true ? attendanceData.statusDetail : null;

  const absentStatus =
    attendanceData?.isPresent === false ? attendanceData.statusDetail : null;

  const studentDisplayName = data.studentName ?? data.name;

  const handleUpdate = async (id: string, isPresent: boolean) => {
    const statusType = isPresent ? 'present' : 'absent';
    setUpdatingType(statusType);
    try {
      await updateAttendance({
        classId,
        student: data._id,
        date: formattedDate,
        attendanceId: id,
        status: statusType,
      });
      toast.success(`Marked ${studentDisplayName} as ${statusType}.`);
      setUpdatingType(null);
      return true;
    } catch (error) {
      console.error(error);
      setUpdatingType(null);
      toast.error(
        getErrorMessage(
          error,
          `Failed to mark ${studentDisplayName} as ${statusType}.`,
        ),
      );
      return false;
    }
  };

  const handleStatusChange = (id: string, isPresent: boolean) => {
    if (attendanceData) {
      setAttendanceStatus({ id, isPresent });
      setShowDialog(true);
    } else {
      void handleUpdate(id, isPresent);
    }
  };

  const handleConfirm = async () => {
    if (attendanceStatus) {
      const success = await handleUpdate(
        attendanceStatus.id,
        attendanceStatus.isPresent,
      );

      if (success) {
        setShowDialog(false);
        setAttendanceStatus(null);
      }
    }
  };

  const presentStatusOptions =
    attendanceStatusData?.filter((status) => status.isPresent) ?? [];
  const absentStatusOptions =
    attendanceStatusData?.filter((status) => !status.isPresent) ?? [];

  return (
    <>
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-row flex-nowrap gap-3',
          className,
        )}
      >
        <div className='min-w-0 flex-1 basis-0'>
          <Select
            value={presentStatus ?? ''}
            onValueChange={(value) => {
              handleStatusChange(value, true);
            }}
            disabled={isUpdatingAttendance}
          >
            <SelectTrigger
              className={cn('w-full border ring-0 focus:ring-0', {
                'border-green-400': !!presentStatus,
              })}
            >
              {isUpdatingToPresent ? (
                <Loader2 className='size-4 shrink-0 animate-spin text-gray-400' />
              ) : (
                <Dot
                  strokeWidth={8}
                  className={cn('text-gray-400', {
                    'text-green-400': !!presentStatus,
                  })}
                />
              )}
              <SelectValue placeholder='Mark Present'>
                {presentStatus}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {presentStatusOptions.map(
                  (status) =>
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    status.statusDetail !== undefined && (
                      <SelectItem key={status._id} value={status._id}>
                        {status.statusDetail}
                      </SelectItem>
                    ),
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className='min-w-0 flex-1 basis-0'>
          <Select
            value={absentStatus ?? ''}
            onValueChange={(value) => {
              handleStatusChange(value, false);
            }}
            disabled={isUpdatingAttendance}
          >
            <SelectTrigger
              className={cn('w-full border ring-0 focus:ring-0', {
                'border-red-600': !!absentStatus,
              })}
            >
              {isUpdatingToAbsent ? (
                <Loader2 className='size-4 shrink-0 animate-spin text-gray-400' />
              ) : (
                <Dot
                  strokeWidth={8}
                  className={cn('text-gray-400', {
                    'text-red-600': !!absentStatus,
                  })}
                />
              )}
              <SelectValue placeholder='Mark Absent'>
                {absentStatus}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {absentStatusOptions.map(
                  (status) =>
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    status.statusDetail !== undefined && (
                      <SelectItem key={status._id} value={status._id}>
                        {status.statusDetail}
                      </SelectItem>
                    ),
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setAttendanceStatus(null);
            setUpdatingType(null);
          }
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {attendanceStatus && (
              <>
                {`The status will be changed to "${
                  attendanceStatusData?.find(
                    (status) => status._id === attendanceStatus.id,
                  )?.statusDetail ?? ''
                }"`}
              </>
            )}
          </DialogDescription>
          <DialogFooter className='gap-2 sm:justify-end'>
            <DialogClose asChild>
              <Button
                variant='outline'
                disabled={isUpdatingAttendance}
                onClick={() => {
                  setAttendanceStatus(null);
                  setUpdatingType(null);
                }}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type='submit'
              onClick={handleConfirm}
              disabled={isUpdatingAttendance}
            >
              {isUpdatingAttendance && <Loader2 className='animate-spin' />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
